import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, BookUser, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { ListRow } from '@/components/ui/Card';
import { SectionCard, SettingsEmpty, SettingsLoading } from './settingsChrome';
import {
  useClinicContacts,
  useCreateClinicContact,
  useUpdateClinicContact,
  useDeleteClinicContact,
  type ClinicContactRow,
  type ClinicContactInput,
} from '@/hooks/useClinicContacts';
import { CLINIC_CONTACT_KINDS, type ClinicContactKind } from '@/lib/patientRelations';
import { contactFieldErrorTexts } from '@/lib/contactFields';

const EMPTY_FORM: ClinicContactInput = {
  name: '',
  kind: 'insurance',
  email: '',
  phone: '',
  cvr: '',
  notes: '',
};

export function ClinicContacts() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const canEdit = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'patients', 'update'),
  );
  const canCreate = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'patients', 'create'),
  );

  const contactsQuery = useClinicContacts(activeClinicId);
  const create = useCreateClinicContact(activeClinicId);
  const update = useUpdateClinicContact(activeClinicId);
  const remove = useDeleteClinicContact(activeClinicId);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ClinicContactRow | null>(null);
  const [form, setForm] = useState<ClinicContactInput>(EMPTY_FORM);
  const [fieldsSubmitted, setFieldsSubmitted] = useState(false);
  const fieldMessages = {
    invalidEmail: t('patients.errors.invalid_email'),
    invalidPhone: t('patients.errors.invalid_phone'),
  };
  const fieldErrors = contactFieldErrorTexts(
    fieldsSubmitted,
    form.email,
    form.phone,
    fieldMessages,
  );

  const contacts = contactsQuery.data ?? [];

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFieldsSubmitted(false);
    setShowForm(true);
  };

  const openEdit = (row: ClinicContactRow) => {
    setEditing(row);
    setForm({
      name: row.name,
      kind: row.kind,
      email: row.email ?? '',
      phone: row.phone ?? '',
      cvr: row.cvr ?? '',
      notes: row.notes ?? '',
    });
    setFieldsSubmitted(false);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setFieldsSubmitted(false);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const fields = contactFieldErrorTexts(true, form.email, form.phone, fieldMessages);
    if (!fields.canSave) {
      setFieldsSubmitted(true);
      return;
    }
    try {
      if (editing) {
        await update.mutateAsync({ ...form, id: editing.id });
        toast.success(t('settings.contacts.toast_updated', 'Contact updated'));
      } else {
        await create.mutateAsync(form);
        toast.success(t('settings.contacts.toast_created', 'Contact created'));
      }
      closeForm();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('settings.contacts.toast_error', 'Could not save contact'),
      );
    }
  };

  const onDelete = async (row: ClinicContactRow) => {
    if (
      !window.confirm(
        t('settings.contacts.confirm_delete', 'Delete {{name}}?', { name: row.name }),
      )
    ) {
      return;
    }
    try {
      await remove.mutateAsync(row.id);
      toast.success(t('settings.contacts.toast_deleted', 'Contact deleted'));
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('settings.contacts.toast_error', 'Could not save contact'),
      );
    }
  };

  return (
    <SectionCard
      title={t('settings.contacts.title')}
      subtitle={t('settings.contacts.subtitle')}
      action={
        canCreate && contacts.length > 0 ? (
          <Button onClick={openCreate} className="w-full nav:w-auto">
            <Plus className="h-4 w-4" aria-hidden />
            {t('settings.contacts.new')}
          </Button>
        ) : undefined
      }
    >
      {contactsQuery.isLoading ? (
        <SettingsLoading />
      ) : contacts.length === 0 ? (
        <SettingsEmpty
          icon={<BookUser className="h-7 w-7" />}
          title={t('settings.contacts.empty')}
          action={
            canCreate ? (
              <Button onClick={openCreate} className="w-full">
                <Plus className="h-4 w-4" aria-hidden />
                {t('settings.contacts.new')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="-mx-s4 nav:-mx-s5">
          {contacts.map((row) => (
            <ListRow key={row.id} className="justify-between">
              <div className="min-w-0 flex-1">
                <p className="truncate type-section text-ds-text dark:text-white">{row.name}</p>
                <p className="truncate type-small text-ds-muted">
                  {t(`patients.relations.contact_kinds.${row.kind}`, row.kind)}
                  {row.phone || row.email
                    ? ` · ${[row.phone, row.email].filter(Boolean).join(' · ')}`
                    : ''}
                </p>
              </div>
              {canEdit && (
                <div className="flex shrink-0 gap-s1">
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    onClick={() => openEdit(row)}
                    aria-label={t('common.edit')}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    size="icon"
                    onClick={() => onDelete(row)}
                    aria-label={t('common.delete')}
                  >
                    <Trash2 />
                  </Button>
                </div>
              )}
            </ListRow>
          ))}
        </div>
      )}

      {showForm && (
        <Modal
          title={
            editing
              ? t('settings.contacts.edit', 'Edit contact')
              : t('settings.contacts.new', 'New contact')
          }
          onClose={closeForm}
        >
          <form onSubmit={onSubmit} className="space-y-3">
            <Input
              label={t('settings.contacts.field_name', 'Name')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Select
              label={t('settings.contacts.field_kind')}
              value={form.kind}
              onChange={(e) =>
                setForm({ ...form, kind: e.target.value as ClinicContactKind })
              }
            >
              {CLINIC_CONTACT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`patients.relations.contact_kinds.${kind}`, kind)}
                </option>
              ))}
            </Select>
            <Input
              label={t('patients.field_phone')}
              name="phone"
              type="tel"
              value={form.phone ?? ''}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              error={fieldErrors.phone}
            />
            <Input
              label={t('patients.field_email')}
              name="email"
              type="text"
              inputMode="email"
              autoComplete="email"
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              error={fieldErrors.email}
            />
            <Input
              label={t('settings.contacts.field_cvr', 'CVR')}
              value={form.cvr ?? ''}
              onChange={(e) => setForm({ ...form, cvr: e.target.value })}
            />
            <Input
              label={t('settings.contacts.field_notes', 'Notes')}
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={closeForm}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending || update.isPending}>
                {t('common.save')}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </SectionCard>
  );
}
